import { useProfilePhoto } from "../../providers/profilePhotoContext";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useExclusiveAction } from '../../hooks/useExclusiveAction';
import { useRequestScope } from '../../hooks/useRequestScope';
import { useScopedState } from '../../hooks/useScopedState';
import { useSessionIdentity } from "../../providers/appBootstrapContext";
import { useAppBootstrap } from "../../providers/AppBootstrapProvider";
import { firestoreService } from "../../services/firestoreService";
import { AuthTopNavProps, getInitials } from './authTopNavPresentation';

const defaultServices = { get firestoreService() { return firestoreService; } };

export function useAuthTopNavViewModel({ anchorLinks = [], includeDefaultLinks = true }: AuthTopNavProps, services = defaultServices) {
  const { firestoreService } = services;
  const sessionIdentity = useSessionIdentity();

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, signOut, currentUser } = useAppBootstrap();
  const scopeKey = JSON.stringify([sessionIdentity, currentUser?.uid]);
  const requestScope = useRequestScope("top-nav", services);
  const activeUser = useRef(currentUser?.uid);
  activeUser.current = currentUser?.uid;
  const [signOutError, setSignOutError] = useScopedState<string | null>(scopeKey, null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useScopedState(scopeKey, false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useScopedState(scopeKey, false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useScopedState<string | null>(scopeKey, null);
  const [profileName, setProfileName] = useScopedState<string>(scopeKey, "");
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const accountTriggerRef = useRef<HTMLButtonElement | null>(null);

  const handleSignOut = useExclusiveAction(requestScope, async () => {
    const isCurrentOperation = requestScope.capture();
    setSignOutError(null);
    try {
      await signOut();
      if (!isCurrentOperation() || (activeUser.current && activeUser.current !== currentUser?.uid)) return;
      navigate("/", { replace: true });
    } catch {
      if (!isCurrentOperation()) return;
      setSignOutError("Unable to sign out.");
      setIsAccountMenuOpen(true);
    }
  });

  useEffect(() => {
    let didCancel = false;

    const loadProfile = async () => {
      if (!currentUser?.uid) {
        setProfilePhotoUrl(null);
        setProfileName("");
        return;
      }

      try {
        const profile = await firestoreService.fetchUserProfile(currentUser.uid);
        if (didCancel) {
          return;
        }

        setProfilePhotoUrl(profile?.profilePhotoURLString || null);
        setProfileName(profile?.userName || currentUser.displayName || currentUser.email || "");
      } catch {
        if (!didCancel) {
          setProfilePhotoUrl(null);
          setProfileName(currentUser.displayName || currentUser.email || "");
        }
      }
    };

    void loadProfile();

    return () => {
      didCancel = true;
    };
  }, [currentUser?.uid, currentUser?.displayName, currentUser?.email, currentUser?.photoURL, setProfilePhotoUrl, setProfileName, firestoreService]);

  useEffect(() => {
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname, setIsAccountMenuOpen, setIsMobileMenuOpen]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!accountMenuRef.current) {
        return;
      }

      if (event.target instanceof Node && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen((current) => {
          if (current) {
            accountTriggerRef.current?.focus();
          }
          return false;
        });
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setIsAccountMenuOpen]);

  const userInitials = useMemo(() => getInitials(profileName), [profileName]);

  const linkClassName = ({ isActive }: { isActive: boolean }) =>
    `auth-topbar-link${isActive ? " is-active" : ""}`;

  const linkStyle = ({ isActive }: { isActive: boolean }) => ({
    color: isActive ? "#ffffff" : "var(--rb-muted-on-dark)",
  });
  const savedPhoto = useProfilePhoto();
  return {
    signOutError,
    isAuthenticated,
    isAdmin,
    isAccountMenuOpen,
    setIsAccountMenuOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    profilePhotoUrl: savedPhoto ? savedPhoto.photoUrl : profilePhotoUrl,
    accountMenuRef,
    accountTriggerRef,
    handleSignOut,
    userInitials,
    linkClassName,
    linkStyle
  };
}
